import { create } from 'zustand';
import type { GeometryKind } from '../geo/types';
import type {
  BaseLayerRecord,
  FeatureRecord,
  MapRecord,
  Status,
  UnityObjectRecord,
  UserRecord,
} from '../lib/types';
import { STATUSES, TYPE_IDS } from '../featureTypes/registry';

export type Tool =
  | { kind: 'none' }
  | { kind: 'draw'; presetId: string; type: string; geometry: GeometryKind }
  | { kind: 'measure' }
  | { kind: 'pick' }
  | { kind: 'calibrate'; layerId: string; step: 'image' | 'world'; pending?: { px: number; py: number } };

export type RealtimeState = 'connecting' | 'connected' | 'offline';

export type AdminTab = 'layers' | 'import' | 'export' | 'users' | 'gamedata' | 'dataexport' | 'fields';

export interface Overlays {
  chunkGrid: boolean;
  grid100: boolean;
  asBuilt: boolean;
  labels: boolean;
  questRoutes: boolean;
}

export interface Filters {
  types: Record<string, boolean>;
  statuses: Record<Status, boolean>;
  onlyMine: boolean;
  tag: string;
}

export interface LayerView {
  visible: boolean;
  opacity: number;
}

export interface Heightmap {
  data: Float32Array;
  minX: number;
  maxZ: number;
  step: number;
  width: number;
  height: number;
}

interface State {
  user: UserRecord | null;
  maps: MapRecord[];
  mapId: string | null;
  loading: boolean;
  features: Record<string, FeatureRecord>;
  baseLayers: BaseLayerRecord[];
  unityObjects: Record<string, UnityObjectRecord>; // by unityKey
  users: Record<string, UserRecord>;
  heightmap: Heightmap | null;
  selectedId: string | null;
  editingShapeId: string | null;
  tool: Tool;
  realtime: RealtimeState;
  overlays: Overlays;
  filters: Filters;
  layerView: Record<string, LayerView>;
  view: 'map' | 'data';
  adminOpen: null | AdminTab;
  toast: { text: string; kind: 'info' | 'error' } | null;
  flyTo: { x: number; z: number; zoom?: number; seq: number } | null;
}

const PREFS_KEY = 'mapeditor.prefs.v1';

function loadPrefs(): Partial<Pick<State, 'overlays' | 'filters' | 'layerView'>> {
  try {
    const raw = localStorage.getItem(PREFS_KEY);
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
}

const prefs = loadPrefs();

export const useStore = create<State>(() => ({
  user: null,
  maps: [],
  mapId: null,
  loading: false,
  features: {},
  baseLayers: [],
  unityObjects: {},
  users: {},
  heightmap: null,
  selectedId: null,
  editingShapeId: null,
  tool: { kind: 'none' },
  realtime: 'connecting',
  overlays: {
    chunkGrid: true,
    grid100: false,
    asBuilt: false,
    labels: true,
    questRoutes: true,
    ...prefs.overlays,
  },
  filters: {
    types: Object.fromEntries(TYPE_IDS.map((t) => [t, true])),
    statuses: Object.fromEntries(STATUSES.map((s) => [s, s !== 'deprecated'])) as Record<Status, boolean>,
    onlyMine: false,
    tag: '',
    ...prefs.filters,
  },
  layerView: prefs.layerView ?? {},
  view: 'map',
  adminOpen: null,
  toast: null,
  flyTo: null,
}));

// Persist per-viewer view preferences (never shared state).
useStore.subscribe((s, prev) => {
  if (s.overlays === prev.overlays && s.filters === prev.filters && s.layerView === prev.layerView) return;
  try {
    localStorage.setItem(
      PREFS_KEY,
      JSON.stringify({ overlays: s.overlays, filters: s.filters, layerView: s.layerView }),
    );
  } catch {
    /* private mode etc. */
  }
});

export const setState = useStore.setState;
export const getState = useStore.getState;

export function currentMap(): MapRecord | null {
  const s = getState();
  return s.maps.find((m) => m.id === s.mapId) ?? null;
}

export function useCurrentMap(): MapRecord | null {
  return useStore((s) => s.maps.find((m) => m.id === s.mapId) ?? null);
}

export function isEditor(u: UserRecord | null): boolean {
  return u?.role === 'editor' || u?.role === 'admin';
}
export function isAdmin(u: UserRecord | null): boolean {
  return u?.role === 'admin';
}

let toastTimer: ReturnType<typeof setTimeout> | undefined;
export function toast(text: string, kind: 'info' | 'error' = 'info') {
  setState({ toast: { text, kind } });
  clearTimeout(toastTimer);
  toastTimer = setTimeout(() => setState({ toast: null }), kind === 'error' ? 7000 : 3000);
}

let flySeq = 0;
export function flyTo(x: number, z: number, zoom?: number) {
  setState({ flyTo: { x, z, zoom, seq: ++flySeq } });
}

export function featureVisible(f: FeatureRecord, s: Pick<State, 'filters' | 'user'>): boolean {
  if (!s.filters.types[f.type]) return false;
  if (!s.filters.statuses[f.status]) return false;
  if (s.filters.onlyMine && f.createdBy !== s.user?.id) return false;
  if (s.filters.tag && !f.tags?.includes(s.filters.tag)) return false;
  return true;
}
